import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "../../environment";
import { TeamService } from "../../feature-module/crm/leads/team.service";
import { cachedRequest } from "../../utils/requestCache";
import { AsyncSelectLoadResult, FormCtx, SelectOption } from "./types";

function authHeaders(): Record<string, string> | null {
  const authHeader = localStorage.getItem("authCred");
  if (!authHeader) return null;
  return {
    "Content-Type": "application/json",
    Authorization: `Basic ${authHeader}`,
  };
}

export const FormLookupsService = {
  fetchLifecycleSources: async (tenantId: string | null): Promise<SelectOption[]> => {
    if (!tenantId) return [];
    const headers = authHeaders();
    if (!headers) return [];
    try {
      return await cachedRequest(`lifecycle-sources:${tenantId}`, async () => {
        const response = await fetch(
          `${API_BASE_URL}/lifecycle-source/?tenant_id=${tenantId}&response_mode=light`,
          { headers }
        );
        if (!response.ok) return [];
        const data = await response.json();
        const results = data.results || data;
        return results
          .filter((item: any) => item.is_active !== false)
          .sort((a: any, b: any) => a.source_order - b.source_order)
          .map((item: any) => ({
            value: item.lifecycle_source_id,
            label: item.lifecycle_source_display_name,
          }));
      });
    } catch (err) {
      return [];
    }
  },

  fetchTeamMembers: async (teamId: number | null): Promise<SelectOption[]> => {
    if (!teamId) return [];
    const headers = authHeaders();
    if (!headers) return [];
    try {
      const response = await fetch(
        `${API_BASE_URL}/team-user/?team_id=${teamId}&response_mode=light`,
        { headers }
      );
      if (!response.ok) return [];
      const data = await response.json();
      const members = Array.isArray(data.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : [];
      return members.map((member: any) => ({
        value: member.user_id || member.user?.user_id || member.user?.id || member.id || member.team_user_id,
        label: member.user_full_name || member.user?.full_name || member.user?.first_name || "Unknown User",
      }));
    } catch (err) {
      return [];
    }
  },

  fetchContactsForCompany: async (
    tenantId: string | null,
    companyId: number | null | undefined,
    page: number
  ): Promise<AsyncSelectLoadResult> => {
    if (!companyId || !tenantId) return { options: [], hasMore: false };
    const headers = authHeaders();
    if (!headers) return { options: [], hasMore: false };
    return cachedRequest(`contacts-for-company:${tenantId}:${companyId}:${page}`, async () => {
      const response = await fetch(
        `${API_BASE_URL}/contact/?tenant_id=${tenantId}&company_id=${companyId}&page=${page}&page_size=50&response_mode=light`,
        { headers }
      );
      if (!response.ok) return { options: [], hasMore: false };
      const data = await response.json();
      const results = Array.isArray(data.results) ? data.results : [];
      return {
        options: results.map((c: any) => ({
          value: c.contact_id,
          label: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
        })),
        hasMore: !!data.next,
      };
    });
  },

  getUserIdFromAuthResponseStorage: (): number | null => {
    const raw = localStorage.getItem("authResponse");
    if (!raw) return null;
    try {
      return JSON.parse(raw).user_id ?? null;
    } catch (err) {
      return null;
    }
  },

  getUserIdWithProfileFallback: (auth: any): number | null => {
    return (
      auth?.user_id ?? auth?.id ?? auth?.profile?.id ?? auth?.profile?.user_id ?? null
    );
  },

  getUserIdWithNestedUserFallback: (auth: any): number | null => {
    if (!auth) return null;
    if (auth.user_id) return auth.user_id;
    if (auth.user?.user_id) return auth.user.user_id;
    if (auth.user?.id) return auth.user.id;

    const raw = localStorage.getItem("authResponse");
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed.user_id) return parsed.user_id;
        if (parsed.user?.user_id) return parsed.user.user_id;
        if (parsed.user?.id) return parsed.user.id;
      } catch (err) {}
    }
    return null;
  },
};

export function fetchContactsForCompany(
  tenantId: string | null,
  companyId: number | null | undefined,
  page: number
): Promise<AsyncSelectLoadResult> {
  return FormLookupsService.fetchContactsForCompany(tenantId, companyId, page);
}

export function useLifecycleSources(tenantId: string | null): SelectOption[] {
  const [options, setOptions] = useState<SelectOption[]>([]);
  useEffect(() => {
    FormLookupsService.fetchLifecycleSources(tenantId).then(setOptions);
  }, [tenantId]);
  return options;
}

export function useContactsCountForCompany(tenantId: string | null) {
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const checkFor = useCallback(
    async (companyId: number | null | undefined) => {
      if (!companyId) {
        setCount(0);
        return;
      }
      setIsLoading(true);
      try {
        const result = await FormLookupsService.fetchContactsForCompany(
          tenantId,
          companyId,
          1
        );
        setCount(result.options.length);
      } catch (err) {
        setCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [tenantId]
  );

  return { count, isLoading, checkFor };
}

export interface TeamAssignment {
  teamOptions: SelectOption[];
  teamMembers: SelectOption[];
  isLoadingTeamMembers: boolean;
  selectedTeamId: number | null;
  onTeamChange: (teamId: number | null, ctx: FormCtx<any>) => void;
}

export function useTeamAssignment(
  tenantId: string | null,
  initialTeamId: number | null,
  opts?: { teamLabel?: (team: any) => string }
): TeamAssignment {
  const teamLabel = opts?.teamLabel || ((team: any) => team.team_name || team.name);

  const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);
  const [teamMembers, setTeamMembers] = useState<SelectOption[]>([]);
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(
    initialTeamId
  );
  const latestTeamRequestRef = useRef<number | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    TeamService.getTeams(parseInt(tenantId))
      .then((result: any[]) =>
        setTeamOptions(
          result.map((team) => ({ value: team.team_id, label: teamLabel(team) }))
        )
      )
      .catch(() => setTeamOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadMembers = useCallback(async (teamId: number | null) => {
    latestTeamRequestRef.current = teamId;
    if (!teamId) {
      setTeamMembers([]);
      return;
    }
    setIsLoadingTeamMembers(true);
    try {
      const members = await FormLookupsService.fetchTeamMembers(teamId);
      if (latestTeamRequestRef.current !== teamId) return;
      setTeamMembers(members);
    } finally {
      if (latestTeamRequestRef.current === teamId) setIsLoadingTeamMembers(false);
    }
  }, []);

  useEffect(() => {
    setSelectedTeamId(initialTeamId);
    loadMembers(initialTeamId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTeamId]);

  const onTeamChange = useCallback(
    (teamId: number | null, ctx: FormCtx<any>) => {
      ctx.setValue("assigned_user_id", null);
      setSelectedTeamId(teamId);
      loadMembers(teamId);
    },
    [loadMembers]
  );

  return { teamOptions, teamMembers, isLoadingTeamMembers, selectedTeamId, onTeamChange };
}

export function buildValidProductsServices(
  items: any[],
  tenantId: string | null
) {
  if (!items || items.length === 0) return [];
  return items
    .filter((item) => item.product_n_service_id && item.quantity > 0)
    .map((item) => ({
      ...(item.lifecycle_product_n_service_id
        ? { lifecycle_product_n_service_id: item.lifecycle_product_n_service_id }
        : {}),
      product_n_service_id: item.product_n_service_id,
      quantity: item.quantity,
      discount_percentage: item.discount_percentage || 0,
      gross_total: item.gross_total || 0,
      discounted_total: item.discounted_total || 0,
      service_tax_percentage: item.service_tax_percentage || 0,
      gst_tax_percentage: item.gst_tax_percentage || 0,
      vat_tax_percentage: item.vat_tax_percentage || 0,
      service_tax_total: item.service_tax_total || 0,
      gst_tax_total: item.gst_tax_total || 0,
      vat_tax_total: item.vat_tax_total || 0,
      tenant_id: tenantId || null,
      override_product_n_service_description:
        item.override_product_n_service_description ?? item.product_n_service_description ?? "",
      override_product_n_service_unit_price: Number(
        item.override_product_n_service_unit_price !== null &&
        item.override_product_n_service_unit_price !== undefined &&
        !isNaN(Number(item.override_product_n_service_unit_price))
          ? item.override_product_n_service_unit_price
          : (item.product_n_service_unit_price ?? 0)
      ),
    }));
}
